import { Button } from "@/src/components/buttons";
import { Card, CheckboxCard, CompartmentCard } from "@/src/components/cards";
import { ListConstructor } from "@/src/components/fetchableLists";
import SearchKeyHighliter from "@/src/components/seachkeyHighlighter";
import {
  CardType,
  Exam,
  FolderItem,
  Patient,
  Sample,
  Service,
} from "@/src/data/allTypes";
import { barcode1 } from "@/src/data/data";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import {
  getAge,
  getHospitalId,
  getItemId,
  getName,
  getUserId,
  getUserShortName,
  printContent,
  uid,
} from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import {
  closeDialog,
  closeDrawer,
  openDeleteDialog,
  openDialog,
  openDrawer,
} from "../../components/dialogs";
import { openEditor } from "../documents/documentsHook";
import { getStandardConfidentiality } from "../patients/confidentialitiesHook";
import { ServicesChoice } from "../hospitalisations/servicesChoice";
import { ReferenceChoiceList } from "../admin/referentiel/referenceChoiceList";

export function SamplesCompartment({
  patient,
  parent,
  type,
}: {
  patient: Patient;
  parent: FolderItem;
  type?: CardType;
}) {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState("");
  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["SamplesCompartment", searchKey, patient.id, reload],
    queryFn: () =>
      apiFetch(
        "samples",
        "loadMany",
        {
          patientId: patient?.id,
          parentId: parent?.id,
        },
        "Erreur lors du chargement des examens"
      ),
  });

  async function chooseSampleType({
    sample,
    onChange,
  }: {
    sample: Sample;
    onChange: (sample: Sample) => void;
  }) {
    await openDrawer({
      id: "sampleChoiceDrawer",
      title: "Choisir un type de prélèvement",
      content: (
        <div>
          <ReferenceChoiceList
            current={[]}
            categories={["sampleType"]}
            onSelect={async (sampleType) => {
              sample.refAnswer ||= {
                creator: getUserId(),
                patient: getItemId(patient),
                fieldAnswers: [],
                notes: "",
                reference: null,
              };
              sample.refAnswer.reference = sampleType;

              onChange(sample);
            }}
          />
        </div>
      ),
    });
  }

  async function chooseService({
    sample,
    onChange,
  }: {
    sample: Sample;
    onChange: (sample: Sample) => void;
  }) {
    await openDrawer({
      id: "serviceChoiceDrawer",
      title: "Choisir un service",
      content: (
        <div>
          <ServicesChoice
            types={["biology"]}
            current={sample.targetService}
            hospitalId={getHospitalId()}
            onChange={async (service) => {
              sample.targetService = service;
              onChange(sample);
            }}
          />
        </div>
      ),
    });
  }

  async function chooseExams({
    sample,
    onChange,
  }: {
    sample: Sample;
    onChange: (sample: Sample) => void;
  }) {
    if (!sample.targetService) throw new Error("Service is required");
    await openDrawer({
      id: "examChoiceDrawer",
      title: "Choisir des examens",
      content: (
        <div>
          <PatientExamsChoiceList
            service={sample.targetService}
            current={[]}
            color="orange"
            patient={patient}
            parent={parent}
            onChange={(exams) => {
              sample.exams = exams;
            }}
          />
        </div>
      ),
      onConfirm: async () => {
        onChange(sample);
      },
    });
  }

  return (
    <CompartmentCard
      color={type || "orange"}
      title={"Prélèvements"}
      secondaryButtonTitle={"Imprimer"}
      onSecondaryClick={async () => {
        await openSamplesPrint({ patient, parent, samples: data || [] });
      }}
      onClick={async () => {
        let sample: Sample = {
          type: "sample",
          numberOfPieces: 1,
          patient: getItemId(patient),
          parent: getItemId(parent),
          barcode: null,
          refAnswer: {
            reference: null,
            creator: getUserId(),
            patient: getItemId(patient),
            fieldAnswers: [],
            notes: "",
          },
          exams: [],
          creator: getUserId(),
          readBy: [],
          confidentiality: getStandardConfidentiality(patient),
          targetService: null,
        };

        await chooseSampleType({
          sample,
          onChange: async (sample) => {
            await chooseService({
              sample,
              onChange: async (sample) => {
                await chooseExams({
                  sample,
                  onChange: async (sample) => {
                    await apiFetch("samples", "addOne", {
                      sample,
                    });
                    closeDrawer("sampleChoiceDrawer");
                    closeDrawer("serviceChoiceDrawer");
                    closeDrawer("examChoiceDrawer");
                    setReload(reload + 1);
                  },
                });
              },
            });
          },
        });
      }}
    >
      <ListConstructor
        data={data}
        isLoading={isLoading}
        isError={isError}
        error={error}
      >
        <div className="grid c1 gap10">
          {data?.map((sample, index) => {
            if (!sample) return;
            return (
              <Card
                key={uid()}
                color={type || "orange"}
                title={
                  <SearchKeyHighliter searchKey={searchKey}>
                    {sample.refAnswer?.reference?.name}
                  </SearchKeyHighliter>
                }
                subTitle={
                  <div className="bold black">{sample.targetService?.name}</div>
                }
                onClick={async () => {
                  await openSampleActionsDialog({ patient, sample });
                }}
                onDelete={async () => {
                  await openDeleteDialog(async () => {
                    await apiFetch("samples", "deleteOne", {
                      sampleId: getItemId(sample),
                    });
                    setReload(reload + 1);
                  });
                }}
              >
                <div className="c1 gap5" style={{ maxWidth: "400px" }}>
                  <div className="gray">
                    le {moment(sample?._date).format("LL HH:mm")} par{" "}
                    {getUserShortName(sample?.creator)}
                  </div>
                  {sample.exams.map((exam) => {
                    if (!exam) return;
                    return (
                      <Card
                        key={uid()}
                        color="orange"
                        title={exam.reference?.name}
                      ></Card>
                    );
                  })}

                  {!sample.externalDemandId && (
                    <Button
                      onClick={async () => {
                        await apiFetch("samples", "apiCreateExamDemand", {
                          sample,
                        });
                      }}
                    >
                      Envoyer vers {sample.targetService?.name}
                    </Button>
                  )}
                  {sample.externalDemandId != null && (
                    <div className="gray">{sample.externalDemandId}</div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </ListConstructor>
    </CompartmentCard>
  );
}

async function openSampleActionsDialog({
  patient,
  sample,
}: {
  patient: Patient;
  sample: Sample;
}) {
  function close() {
    closeDialog("sampleActionsDialog");
  }

  return await openDialog({
    id: "sampleActionsDialog",
    title: "Actions",
    content: (
      <div className="c1 gap10">
        <Button
          onClick={async () => {
            close();
            await printBiologyExamTicket({
              patient,
              sample,
            });
          }}
        >
          Imprimer
        </Button>
        <Button
          type="tertiary"
          onClick={async () => {
            close();
          }}
        >
          Modifier
        </Button>
        <Button
          color="red"
          type="tertiary"
          onClick={async () => {
            close();
          }}
        >
          Supprimer
        </Button>
      </div>
    ),
  });
}

function PatientExamsChoiceList({
  current,
  service,
  color,
  patient,
  parent,
  onChange,
}: {
  current: Exam[];
  service: Service;
  color: CardType;
  patient: Patient;
  parent: FolderItem;
  onChange: (newValue: Exam[]) => void;
}) {
  const [selectedExams, setSelectedExams] = useState(current);
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState("");
  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["PatientExamsChoiceList", searchKey, patient.id, reload],
    queryFn: () =>
      apiFetch(
        "exams",
        "loadMany",
        {
          patientId: patient.id,
          categories: ["exam", "biology", "other"],

          statuses: [
            "pending",
            "inProgress",
            "validated",
            "waitingForValidation",
          ],
          parentId: parent?.id,
        },
        "Erreur lors du chargement des examens"
      ),
  });

  function handleChange(exam: Exam) {
    let newSelectedExams = [...selectedExams];
    if (newSelectedExams.find((e) => e.id == exam.id)) {
      newSelectedExams = newSelectedExams.filter((e) => e.id != exam.id);
    } else {
      newSelectedExams = [...newSelectedExams, exam];
    }
    setSelectedExams([...newSelectedExams]);
    onChange([...newSelectedExams]);
  }

  console.log("service.appointmentTypes", service.appointmentTypes);

  return (
    <ListConstructor
      key={uid()}
      data={data?.exams}
      isLoading={isLoading}
      isError={isError}
      error={error}
    >
      <div className="grid c1 gap10">
        {data?.exams.map((exam, index) => {
          if (!exam) return;
          let isAllowed = service.appointmentTypes?.find(
            (appointmentType) =>
              getItemId(appointmentType?.exam) == getItemId(exam.reference)
          );
          if (!isAllowed) return;
          console.log(exam);
          return (
            <CheckboxCard
              key={uid()}
              color={color || "blue"}
              className="empty"
              title={
                <SearchKeyHighliter searchKey={searchKey}>
                  {exam.reference?.name}
                </SearchKeyHighliter>
              }
              status={
                selectedExams.find((e) => e.id == exam.id)
                  ? "active"
                  : "inactive"
              }
              subTitle={getUserShortName(exam?.creator)}
              onClick={async (e) => {
                handleChange(exam as Exam);
              }}
            >
              <div className="gray">
                {moment(exam?._date).format("LL HH:mm")}
              </div>
              <div className="gray">
                {exam.samples?.map((sample) => {
                  if (!sample) return null;
                  return (
                    <Card
                      key={uid()}
                      title={<div>Prélèvement N°{getItemId(sample)}</div>}
                      color="orange"
                    >
                      {sample?.refAnswer?.reference?.name}
                    </Card>
                  );
                })}
              </div>
            </CheckboxCard>
          );
        })}
      </div>
    </ListConstructor>
  );
}

export async function openSamplesPrint({
  patient,
  parent,
  samples,
}: {
  patient: Patient;
  parent: FolderItem | null | undefined;
  samples: Sample[];
}) {
  let contentObj: Record<string, string> = {};
  for (let sample of samples) {
    if (sample.exams.length == 0) continue;
    let serviceId = getItemId(sample.targetService);
    contentObj[serviceId] ||= "";
    contentObj[
      serviceId
    ] += `<div class=" gray-bg br c2 gap10 acenter" style="padding:0px 20px;">
      <div class="c1 gap5">
        <div class="bold">${sample.refAnswer?.reference?.name}</div>
        <div class="bold f15">- ${sample.exams
          .map((exam) => exam?.reference?.name)
          .join("\n- ")}</div>
      </div>
      <div class="tright right">

        <div class="gray">${barcode1}</div>
        <div class="gray">${sample.id || "-"}</div>
      </div>
    </div>`;
  }

  let contentStr = "";

  for (let serviceId in contentObj) {
    let service = samples.find(
      (sample) => getItemId(sample.targetService) == serviceId
    )?.targetService;
    contentStr += `<div class="c1 gap20 ">
      <div class="bold f15">${service?.name}</div>
      <div class="grid c1 gap10">${contentObj[serviceId]}</div>
    </div><br><br>`;
  }

  await openEditor({
    patient,
    patientDocument: {
      parent,
      template: {
        creator: USER.get(),
        format: "a4",
        isValidatable: false,
        name: "Demande d'examen",
        title: "Demande d'examen",
        parent: null,
        showLeftBar: false,
        barcodeLocation: "none",
        showTitle: true,
        showHeader: true,
        showPatientIdentity: true,
        content: ``,
      },
      title: "Demande d'examen",
      content: `<div class="c1 gap20">${contentStr}</div>`,
      creator: getUserId(),
      patient,
      refAnswer: null,
      status: "pending",
    },
    onPrint: () => {},
  });
}

export async function printBiologyExamTicket({
  patient,
  sample,
}: {
  patient: Patient;
  sample: Sample;
}) {
  if (!sample) throw new Error("Sample not found");

  let barcodeStr = `<div>${barcode1}</div><div class="gray">${
    sample.externalDemandId || "-"
  }</div>`;

  let html = `<div style="display:block; width:100%; height:110px;  overflow:hidden; border:none; font-size:20px; text-align:center; background:white; background:RGBA(0,0,0,0.2);">${barcodeStr}<strong>${
    sample?.refAnswer?.reference?.name
  }</strong>
  <strong>${sample.exams
    ?.map((exam) => exam?.reference?.name)
    .join(", ")}</strong><br/>
  <span class="capitalize">${getName(patient)}</span> - ${getAge(
    patient
  )}</div>`;
  printContent(html, null, USER.get()?.service);
}
